import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Calendar, User, Clock, ArrowRight, ArrowLeft } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { SEOHead } from './SEOHead';
import { PageFaq, TextBlocks } from './PageAeo';
import { useSiteSettings, getPageSeo, buildFaqSchema, safeUrl, toSlug, NewsArticle } from '../services/SiteSettings';
import { SITE_URL } from '../data/organizationSchema.mjs';

interface NewsPageProps {
  initialArticleSlug?: string;
  onArticleNavigate?: (slug: string | null) => void;
}

const CATEGORY_COLORS = [
  'bg-blue-100 text-blue-800',
  'bg-green-100 text-green-800',
  'bg-purple-100 text-purple-800',
  'bg-orange-100 text-orange-800',
  'bg-indigo-100 text-indigo-800',
  'bg-red-100 text-red-800',
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const readTime = (a: NewsArticle) => {
  const words = `${a.excerpt} ${a.body}`.split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
};

const absoluteUrl = (src: string) => (src.startsWith('/') ? `${SITE_URL}${src}` : src);

export function NewsPage({ initialArticleSlug, onArticleNavigate }: NewsPageProps) {
  const settings = useSiteSettings();
  const seo = getPageSeo(settings, '/news');

  const articles = useMemo(
    () =>
      (settings.news.articles || [])
        .filter((a) => a.title)
        .map((a) => ({ ...a, slug: a.slug || toSlug(a.title), takeaways: a.takeaways || [] }))
        .sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    [settings.news.articles]
  );

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(articles.map((a) => a.category).filter(Boolean)))],
    [articles]
  );
  const categoryColor = (category: string) =>
    CATEGORY_COLORS[Math.max(0, categories.indexOf(category) - 1) % CATEGORY_COLORS.length];

  const [selectedSlug, setSelectedSlug] = useState<string | null>(initialArticleSlug || null);
  const [activeCategory, setActiveCategory] = useState('All');
  const selectedArticle = selectedSlug ? articles.find((a) => a.slug === selectedSlug) || null : null;

  const openArticle = (article: NewsArticle) => {
    setSelectedSlug(article.slug);
    window.history.pushState({}, '', `/news/${article.slug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
    onArticleNavigate?.(article.slug);
  };

  const closeArticle = () => {
    setSelectedSlug(null);
    window.history.pushState({}, '', '/news');
    window.scrollTo({ top: 0, behavior: 'instant' });
    onArticleNavigate?.(null);
  };

  const visible = activeCategory === 'All' ? articles : articles.filter((a) => a.category === activeCategory);
  const featuredArticles = visible.filter((a) => a.featured);
  const regularArticles = visible.filter((a) => !a.featured);

  const articleSchema = (a: NewsArticle) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": a.title,
    "description": a.excerpt,
    "datePublished": a.date,
    "dateModified": a.date,
    ...(safeUrl(a.image) ? { "image": absoluteUrl(safeUrl(a.image)) } : {}),
    "author": a.author ? { "@type": "Person", "name": a.author } : { "@type": "Organization", "name": settings.contact.name },
    "publisher": {
      "@type": "Organization",
      "name": settings.contact.name,
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/usp.png` }
    },
    "articleSection": a.category || undefined,
    "mainEntityOfPage": `${SITE_URL}/news/${a.slug}`
  });

  if (selectedArticle) {
    return (
      <div className="min-h-screen py-16">
        <SEOHead
          title={selectedArticle.title}
          description={selectedArticle.excerpt}
          canonical={`${SITE_URL}/news/${selectedArticle.slug}`}
          ogImage={safeUrl(selectedArticle.image) ? absoluteUrl(safeUrl(selectedArticle.image)) : undefined}
          ogType="article"
          keywords={`${selectedArticle.category}, ${settings.contact.name} news, Nepal development`}
          publishedTime={selectedArticle.date}
          author={selectedArticle.author || settings.contact.name}
          takeaways={selectedArticle.takeaways}
          structuredData={[
            articleSchema(selectedArticle),
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": "News", "item": `${SITE_URL}/news` },
                { "@type": "ListItem", "position": 3, "name": selectedArticle.title, "item": `${SITE_URL}/news/${selectedArticle.slug}` }
              ]
            }
          ]}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={closeArticle}
            className="flex items-center space-x-2 text-gray-600 hover:text-primary mb-8 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to News</span>
          </button>
          <article>
            <header className="mb-8">
              {selectedArticle.category && (
                <Badge className={categoryColor(selectedArticle.category)}>{selectedArticle.category}</Badge>
              )}
              <h1 className="mt-4 mb-4">{selectedArticle.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <time dateTime={selectedArticle.date}>{formatDate(selectedArticle.date)}</time>
                </div>
                {selectedArticle.author && (
                  <div className="flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>{selectedArticle.author}</span>
                  </div>
                )}
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{readTime(selectedArticle)}</span>
                </div>
              </div>
              <p className="text-lg text-gray-800 font-medium leading-relaxed">{selectedArticle.excerpt}</p>
            </header>
            {safeUrl(selectedArticle.image) && (
              <ImageWithFallback
                src={safeUrl(selectedArticle.image)}
                alt={selectedArticle.title}
                className="w-full h-72 object-cover rounded-lg mb-8"
              />
            )}
            <TextBlocks text={selectedArticle.body} className="text-gray-700 leading-relaxed" />
          </article>
        </div>
      </div>
    );
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
      { "@type": "ListItem", "position": 2, "name": "News", "item": `${SITE_URL}/news` }
    ]
  };

  const articleListSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": seo.title || 'News & Updates',
    "description": seo.description,
    "url": `${SITE_URL}/news`,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": articles.map((a, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `${SITE_URL}/news/${a.slug}`,
        "name": a.title
      }))
    }
  };
  const faqSchema = buildFaqSchema(seo.faqs);

  const ArticleCard = ({ article, featured }: { article: NewsArticle; featured?: boolean }) => (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className="relative">
        <ImageWithFallback
          src={safeUrl(article.image) || '/usp.png'}
          alt={article.title}
          className={`w-full ${featured ? 'h-64' : 'h-48'} object-cover`}
        />
        {article.category && (
          <div className="absolute top-4 left-4">
            <Badge className={categoryColor(article.category)}>{article.category}</Badge>
          </div>
        )}
        {featured && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-yellow-500 text-white">Featured</Badge>
          </div>
        )}
      </div>
      <CardHeader>
        <h3 className={`font-semibold leading-snug ${featured ? 'text-xl' : 'text-lg'} line-clamp-2`}>{article.title}</h3>
        <div className="flex items-center space-x-4 text-sm text-gray-500">
          <div className="flex items-center space-x-1">
            <Calendar className="w-4 h-4" />
            <time dateTime={article.date}>{formatDate(article.date)}</time>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>{readTime(article)}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{article.excerpt}</p>
        <Button variant="outline" size="sm" className="w-full cursor-pointer" onClick={() => openArticle(article)}>
          Read More
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'News & Updates'}
        description={seo.description}
        canonical={`${SITE_URL}/news`}
        keywords={seo.keywords}
        takeaways={seo.takeaways}
        structuredData={[breadcrumbSchema, articleListSchema, ...(faqSchema ? [faqSchema] : [])]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <h1 className="mb-6">News & Updates</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">{seo.description}</p>
        </header>

        {articles.length === 0 ? (
          <p className="text-center text-gray-500 py-12">No news has been published yet. Please check back soon.</p>
        ) : (
          <>
            {categories.length > 2 && (
              <div className="flex flex-wrap gap-2 mb-12 justify-center">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={category === activeCategory ? 'default' : 'outline'}
                    size="sm"
                    className="text-sm cursor-pointer"
                    onClick={() => setActiveCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            )}

            {featuredArticles.length > 0 && (
              <section className="mb-16">
                <h2 className="mb-8">Featured News</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {featuredArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} featured />
                  ))}
                </div>
              </section>
            )}

            {regularArticles.length > 0 && (
              <section>
                <h2 className="mb-8">Latest News</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {regularArticles.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      <div className="mt-16">
        <PageFaq faqs={seo.faqs} />
      </div>
    </div>
  );
}
